import {Product} from '@mintdata-wsp/common/controllers/network/generated/responses/Product';
import {ProductVariant} from '@mintdata-wsp/common/controllers/network/generated/responses/ProductVariant';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ProductType} from '@mintdata-wsp/common/state/StorefrontState';
import {ChargePeriod} from '@mintdata-wsp/common/state/ui/storefront/ChargePeriod';
import {PriceGrantUnits} from '@mintdata-wsp/common/state/ui/storefront/PriceGrantUnits';
import {
    AddonGrant,
    CloudAccessGrant,
    ProductGrant,
    ProductGrantParser
} from '@mintdata-wsp/common/state/ui/storefront/ProductGrant';
import {ProductUtil} from '@mintdata-wsp/wsp/controllers/workspace/settings/storefront/ProductUtil';
import {action} from 'mobx';

export type ProductInfo =
    {
        product:Product,
        productVariant:ProductVariant,
        productGrant:ProductGrant
    };

export class ProductSelectionController
{
    isAnnualChargePeriodSelected():boolean | null
    {
        const {productSelectionState} = rootState.ui;
        const selectedChargePeriod = productSelectionState.selectedChargePeriod;

        if( !selectedChargePeriod )
            return null;

        return ChargePeriod.Annual.isEqualTo(selectedChargePeriod);
    }

    isMonthlyChargePeriodSelected():boolean | null
    {
        const {productSelectionState} = rootState.ui;
        const selectedChargePeriod = productSelectionState.selectedChargePeriod;

        if( !selectedChargePeriod )
            return null;

        return ChargePeriod.Month.isEqualTo(selectedChargePeriod);
    }

    findMonthlyVariant(product:Product):ProductVariant | null
    {
        return this._findProductVariant(product, ChargePeriod.Month);
    }

    findYearlyVariant(product:Product):ProductVariant | null
    {
        return this._findProductVariant(product, ChargePeriod.Annual);
    }

    private _findProductVariant(product:Product, periodToFind:ChargePeriod):ProductVariant | null
    {
        if( !product || !periodToFind )
            return null;

        const productVariant:ProductVariant = product.variants
            .find((pv:ProductVariant) =>
            {
                const pricePeriod = ChargePeriod.fromString(pv.pricePeriod);
                if( !pricePeriod )
                    return null;

                return periodToFind.isEqualTo(pricePeriod);
            });

        return productVariant;
    }

    getAnnualSavingsTotal():string | null
    {
        const {productSelectionState} = rootState.ui;
        const selectedChargePeriod = productSelectionState.selectedChargePeriod;

        if( !selectedChargePeriod )
            return null;

        if( ChargePeriod.Month.isEqualTo(selectedChargePeriod) )
            return null;

        const annualTotalUsingMonthlyPrices = this._getAnnualTotalUsingMonthlyPrices();
        const annualTotalDisplayedToUser = this._getRecieptTotalNumber();
        const savingsByPayingAnnually = annualTotalUsingMonthlyPrices - annualTotalDisplayedToUser;

        return ChargePeriod.formatPrice(savingsByPayingAnnually);
    }

    private _getAnnualTotalUsingMonthlyPrices():number | null
    {
        const {productSelectionState} = rootState.ui;

        let cloudAccess = productSelectionState.selectedCloudAccess;
        if( !cloudAccess )
            return null;

        let addons = productSelectionState.selectedAddons;
        if( !addons )
            return null;

        let total:number = Number(0);

        const cloudAccessPerMonth:ProductVariant = this.findMonthlyVariant(cloudAccess);
        const cloudAccessYearly:number = cloudAccessPerMonth.price * ChargePeriod.MONTHS_PER_YEAR;

        total += cloudAccessYearly;

        for( const addon of addons )
        {
            const addonPerMonth:ProductVariant = this.findMonthlyVariant(addon);
            const addonYearly:number = addonPerMonth.price * ChargePeriod.MONTHS_PER_YEAR;

            total += addonYearly;
        }

        return total;
    }

    getReceiptTotalInPerMonthUnits():string | null
    {
        const {productSelectionState} = rootState.ui;
        const selectedChargePeriod = productSelectionState.selectedChargePeriod;

        if( !selectedChargePeriod )
            return null;

        if( ChargePeriod.Month.isEqualTo(selectedChargePeriod) )
            return this.getReceiptTotal();
        else if( ChargePeriod.Annual.isEqualTo(selectedChargePeriod) )
        {
            const annualTotal:number = this._getRecieptTotalNumber();
            if( !annualTotal )
                return null;

            const perMonthTotal:number = annualTotal / ChargePeriod.MONTHS_PER_YEAR;
            return ChargePeriod.formatPrice(perMonthTotal);
        }
        else
            return null;
    }

    getReceiptTotal():string | null
    {
        const total:number = this._getRecieptTotalNumber();
        if( !total )
            return null;

        return ChargePeriod.formatPrice(total);
    }

    private _getRecieptTotalNumber():number | null
    {
        const selectedVariants = this._getCurrentlySelectedVariants();
        if( !selectedVariants )
            return null;

        let total:number = Number(0.00);
        total += selectedVariants.cloudAccessVariant.price;

        const addonVariants = selectedVariants.addonVariants;

        for( const addonVariant of addonVariants )
            total += addonVariant.price;

        return total;
    }

    private _getCurrentlySelectedVariants():{cloudAccessVariant:ProductVariant, addonVariants:ProductVariant[]} | null
    {
        const {productSelectionState} = rootState.ui;

        const selectedCloudAccess = productSelectionState.selectedCloudAccess;
        if( !selectedCloudAccess )
            return null;

        const selectedAddons = productSelectionState.selectedAddons;
        if( !selectedAddons )
            return null;

        const currentCloudVariant = this.getCurrentVariant(selectedCloudAccess);
        if( !currentCloudVariant )
            return null;

        const addonVariants:ProductVariant[] = [];
        for( const selectedAddon of selectedAddons )
        {
            let currentVariant = this.getCurrentVariant(selectedAddon);
            if( !currentVariant )
                return null;

            addonVariants.push(currentVariant);
        }

        return {cloudAccessVariant: currentCloudVariant, addonVariants: addonVariants};
    }

    @action.bound
    selectChargePeriod(chargePeriod:ChargePeriod):void
    {
        const {productSelectionState} = rootState.ui;
        productSelectionState.selectedChargePeriod = chargePeriod;
    }

    isChargePeriodSelected(chargePeriod:ChargePeriod):boolean
    {
        const {productSelectionState} = rootState.ui;
        return chargePeriod && chargePeriod.isEqualTo(productSelectionState.selectedChargePeriod);
    }

    isCloudAccessSelected(cloudAccessProduct:Product):boolean
    {
        const {productSelectionState} = rootState.ui;
        return ProductUtil.isEqual(productSelectionState.selectedCloudAccess, cloudAccessProduct);
    }

    getPriceInPerMonthTerms(product:Product, productVariant:ProductVariant):number | null
    {
        const {productSelectionState} = rootState.ui;
        const selectedCloudAccess = productSelectionState.selectedCloudAccess;

        if( !selectedCloudAccess )
            return null;

        const productGrant:ProductGrant = ProductGrantParser.parseFromString(selectedCloudAccess.grant);
        if( !productGrant )
            return null;

        if( productGrant.productType !== ProductType.Cloud_Access )
            return null;

        const cloudAccessGrant = (productGrant as CloudAccessGrant);
        const priceGrantUnits = PriceGrantUnits.fromString(productVariant.priceGrantUnits);

        if( !priceGrantUnits )
            return null;

        let multiplyPriceBy:number = null;
        if( priceGrantUnits.isEqualTo(PriceGrantUnits.Organization) )
            multiplyPriceBy = 1.0;
        else if( priceGrantUnits.isEqualTo(PriceGrantUnits.User) )
            multiplyPriceBy = cloudAccessGrant.maxUsers;
        else if( priceGrantUnits.isEqualTo(PriceGrantUnits.Application) )
            multiplyPriceBy = cloudAccessGrant.maxApplications;
        else
            return null;

        const monthlyOrAnnualPrice = productVariant.price * multiplyPriceBy;

        if( ChargePeriod.Month.isPricePeriodOf(productVariant) )
            return monthlyOrAnnualPrice;
        else
            return monthlyOrAnnualPrice / ChargePeriod.MONTHS_PER_YEAR;
    }

    getCurrentCloudAccess():ProductInfo | null
    {
        const {productSelectionState} = rootState.ui;
        const {selectedCloudAccess} = productSelectionState;

        if( !selectedCloudAccess )
            return null;

        const productVariant = this.getCurrentVariant(selectedCloudAccess);
        if( !productVariant )
            return null;

        const productGrant = ProductGrantParser.parseFromString(selectedCloudAccess.grant) as CloudAccessGrant;
        if( !productGrant )
            return null;

        return {
            product: selectedCloudAccess,
            productVariant: productVariant,
            productGrant: productGrant
        } as ProductInfo;
    }

    getNotSelectedAddons():ProductInfo[] | null
    {
        const {productOffering} = rootState.data.storefront;
        if( !productOffering )
            return null;

        const products = productOffering.products;
        if( !products )
            return null;

        const allAddons:Product[] = products.filter((p:Product) =>
        {
            return p.type === ProductType.Addon;
        });

        const notSelectedAddons:Product[] = allAddons.filter((addon:Product) =>
        {
            return !this.isAddonSelected(addon);
        });

        return this._addonsToProductInfo(notSelectedAddons);
    }

    getCurrentAddons():ProductInfo[] | null
    {
        const {productSelectionState} = rootState.ui;
        const {selectedAddons} = productSelectionState;

        if( !selectedAddons )
            return null;

        return this._addonsToProductInfo(selectedAddons);
    }

    private _addonsToProductInfo(addons:Product[]):ProductInfo[] | null
    {
        let productInfos:ProductInfo[] = [];

        for( const addon of addons )
        {
            const productVariant = this.getCurrentVariant(addon);
            if( !productVariant )
                return null;

            const productGrant = ProductGrantParser.parseFromString(addon.grant) as AddonGrant;
            if( !productGrant )
                return null;

            productInfos.push({
                product: addon,
                productVariant: productVariant,
                productGrant: productGrant
            });
        }

        return productInfos;
    }

    getCurrentVariant(product:Product):ProductVariant | null
    {
        const {productSelectionState} = rootState.ui;
        const {selectedChargePeriod} = productSelectionState;

        const currentVariant = product.variants.find((productVariant:ProductVariant) =>
        {
            return selectedChargePeriod.isPricePeriodOf(productVariant);
        });

        return currentVariant ? currentVariant : null;
    }

    @action.bound
    selectCloudAccess(cloudAccessProduct:Product):void
    {
        if( cloudAccessProduct.type !== ProductType.Cloud_Access )
            throw new Error('Cannot selectCloudAccess(): invalid productType, product: ' + JSON.stringify(cloudAccessProduct));

        const {productSelectionState} = rootState.ui;
        productSelectionState.selectedCloudAccess = cloudAccessProduct;
    }

    isAddonSelected(addonProduct:Product):boolean
    {
        return this._isAddonSelected(addonProduct).isSelected;
    }

    toggleAddonSelection(addonProduct:Product):void
    {
        if( this.isAddonSelected(addonProduct) )
            this.deselectAddon(addonProduct);
        else
            this.selectAddon(addonProduct);
    }

    @action.bound
    selectAddon(addonProduct:Product):void
    {
        if( addonProduct.type !== ProductType.Addon )
            throw new Error('Cannot selectAddonProduct(): invalid productType, product: ' + JSON.stringify(addonProduct));

        const {productSelectionState} = rootState.ui;
        const selection = this._isAddonSelected(addonProduct);

        if( !selection.isSelected )
            productSelectionState.selectedAddons.push(addonProduct);
    }

    @action.bound
    deselectAddon(addonProduct:Product):void
    {
        if( addonProduct.type !== ProductType.Addon )
            throw new Error('Cannot selectAddonProduct(): invalid productType, product: ' + JSON.stringify(addonProduct));

        const {productSelectionState} = rootState.ui;
        const selection = this._isAddonSelected(addonProduct);

        if( selection.isSelected )
            productSelectionState.selectedAddons.splice(selection.selectionIndex, 1);
    }

    private _isAddonSelected(addonProduct:Product):{isSelected:boolean, selectionIndex:number}
    {
        const {productSelectionState} = rootState.ui;
        let selectionIndex = 0;

        if( !productSelectionState.selectedAddons )
            return {isSelected: false, selectionIndex: -1};

        const alreadySelected = productSelectionState.selectedAddons.find((p:Product, index:number) =>
        {
            const isFound = ProductUtil.isEqual(p, addonProduct);

            if( isFound )
                selectionIndex = index;

            return isFound;
        });

        return {isSelected: !!alreadySelected, selectionIndex};
    }
}
